import type { RealtimeNotification } from './realtime.js';

const MAX_ITEMS = 200;

type Listener = () => void;

const listeners = new Set<Listener>();
let items: RealtimeNotification[] = [];
let unreadCount = 0;

function notificationKey(n: RealtimeNotification): string {
  return n.id ?? `${n.kind}|${n.title}|${n.targetEntity ?? ''}|${n.targetId ?? ''}|${n.ts}`;
}

export const notificationStore = {
  getItems: () => items,
  getUnread: () => unreadCount,

  push(n: RealtimeNotification) {
    const key = notificationKey(n);
    items = [n, ...items.filter((item) => notificationKey(item) !== key)].slice(0, MAX_ITEMS);
    unreadCount = countUnread(items);
    notify();
  },

  hydrate(nextItems: RealtimeNotification[]) {
    const seen = new Set<string>();
    items = [...nextItems, ...items]
      .filter((item) => {
        const key = notificationKey(item);
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      })
      .sort((a, b) => new Date(b.ts).getTime() - new Date(a.ts).getTime())
      .slice(0, MAX_ITEMS);
    unreadCount = countUnread(items);
    notify();
  },

  markAllRead() {
    unreadCount = 0;
    const now = new Date().toISOString();
    items = items.map((item) => ({ ...item, readAt: item.readAt ?? now }));
    notify();
  },

  markOneRead(id?: string) {
    if (!id) {
      unreadCount = Math.max(0, unreadCount - 1);
      notify();
      return;
    }
    const now = new Date().toISOString();
    items = items.map((item) => (item.id === id ? { ...item, readAt: item.readAt ?? now } : item));
    unreadCount = countUnread(items);
    notify();
  },

  reset() {
    items = [];
    unreadCount = 0;
    notify();
  },

  subscribe(fn: Listener): () => void {
    listeners.add(fn);
    return () => listeners.delete(fn);
  },
};

function notify() {
  for (const fn of listeners) fn();
}

function countUnread(nextItems: RealtimeNotification[]): number {
  return nextItems.filter((item) => !item.readAt).length;
}
